import React, { useState } from 'react';
import { motion } from 'framer-motion';

const Services = () => {
  const [activeTab, setActiveTab] = useState('ai');

  const services = [
    {
      id: 'ai',
      title: 'AI & Machine Learning',
      icon: '🤖',
      description: 'Cutting-edge artificial intelligence solutions that transform your business operations.',
      features: [
        'Predictive Analytics',
        'Natural Language Processing',
        'Computer Vision',
        'Recommendation Systems',
        'Automated Decision Making',
        'Real-time Data Processing'
      ],
      price: 'From $5,000/month'
    },
    {
      id: 'automation',
      title: 'Process Automation',
      icon: '⚡',
      description: 'Streamline workflows and eliminate manual tasks with intelligent automation.',
      features: [
        'Workflow Automation',
        'RPA Implementation',
        'API Integration',
        'Data Migration',
        'Quality Assurance',
        'Performance Monitoring'
      ],
      price: 'From $3,500/month'
    },
    {
      id: 'analytics',
      title: 'Data Analytics',
      icon: '📊',
      description: 'Turn your data into actionable insights with advanced analytics and visualization.',
      features: [
        'Business Intelligence',
        'Data Visualization',
        'Statistical Analysis',
        'Performance Dashboards',
        'Custom Reporting',
        'Data Governance'
      ],
      price: 'From $2,500/month'
    },
    {
      id: 'cloud',
      title: 'Cloud Solutions',
      icon: '☁️',
      description: 'Scalable cloud infrastructure and migration services for modern businesses.',
      features: [
        'Cloud Migration',
        'Infrastructure Setup',
        'DevOps Implementation',
        'Security & Compliance',
        'Cost Optimization',
        '24/7 Monitoring'
      ],
      price: 'From $4,000/month'
    }
  ];

  const activeService = services.find(service => service.id === activeTab);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-50 to-secondary-50 section-padding">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Our <span className="text-gradient">Services</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive technology solutions designed to accelerate your business growth and digital transformation.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Service Tabs */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              What We Offer
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Choose from our range of specialized services tailored to meet your specific business needs.
            </p>
          </motion.div>

          {/* Tab Navigation */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12"
          >
            {services.map((service) => (
              <motion.button
                key={service.id}
                variants={itemVariants}
                onClick={() => setActiveTab(service.id)}
                className={`p-6 rounded-xl text-center transition-all duration-300 ${
                  activeTab === service.id
                    ? 'bg-primary-600 text-white shadow-lg scale-105'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <div className="text-3xl mb-3">{service.icon}</div>
                <h3 className="font-semibold text-sm md:text-base">{service.title}</h3>
              </motion.button>
            ))}
          </motion.div>

          {/* Active Service Details */}
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 md:p-12"
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="text-6xl mb-6">{activeService.icon}</div>
                <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                  {activeService.title}
                </h3>
                <p className="text-lg text-gray-600 mb-6">
                  {activeService.description}
                </p>
                <div className="text-2xl font-bold text-primary-600 mb-6">
                  {activeService.price}
                </div>
                <button className="btn-primary text-lg px-8 py-3">
                  Get Started
                </button>
              </div>
              <div>
                <h4 className="text-xl font-semibold text-gray-900 mb-4">
                  What's Included:
                </h4>
                <ul className="space-y-3">
                  {activeService.features.map((feature, index) => (
                    <motion.li
                      key={index}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.1 }}
                      className="flex items-center text-gray-700"
                    >
                      <svg className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      {feature}
                    </motion.li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Process Section */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Our Process
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A proven methodology that ensures successful project delivery and maximum value for your business.
            </p>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-4 gap-8"
          >
            {[
              { step: '01', title: 'Discovery', description: 'We analyze your needs and define project scope' },
              { step: '02', title: 'Planning', description: 'Detailed project planning and architecture design' },
              { step: '03', title: 'Development', description: 'Agile development with regular check-ins' },
              { step: '04', title: 'Deployment', description: 'Smooth launch and ongoing support' }
            ].map((process, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className="text-center"
              >
                <div className="bg-primary-600 text-white text-2xl font-bold rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  {process.step}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {process.title}
                </h3>
                <p className="text-gray-600">
                  {process.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Services;
